import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";

// Student interface matching exact backend DTO structure
export interface Student {
  id: number;
  studentCode: string; // Student code field
  applicationNo: string;
  rollNumber: string;
  enrolmentNo: string;
  studentName: string; // Required field marked with *
  fatherName: string;
  motherName: string;
  dateOfBirth: string | number[]; // Can be string or array format [year, month, day]
  gender: string;
  medium: string;
  caste: string;
  subCaste: string;
  casteCertificate: string;
  religion: string;
  sssmid: string;
  scholarNo: string;
  penNo: string;
  apparId: string;
  aadharNo: string;
  houseNo: string;
  street: string;
  landmark: string;
  area: string;
  city: string;
  postOffice: string;
  district: string;
  state: string;
  pinCode: string;
  mobileNo: string;
  bankName: string;
  ifcCode: string;
  accountNo: string;
  annualIncome: string;
  section: string;
  subjects: string | string[]; // Allow both string and array for flexibility
  gmail: string; // Required field marked with *
  admissionDate: string | number[]; // Can be string or array format [year, month, day]
  admissionClass: string;
  admissionType: string; // Admission Type field (REGULAR or PRIVATE)
  stream: string; // Stream field
  schoolId: number; // Required field marked with *
  schoolClassId: number;
  sessionName: string;
  sessionId: number;
  className: string; // Required field marked with *
  userId?: number;
  status: boolean;
  userStatus?: boolean; // User login status (enabled/disabled)
  studentPhoto?: string | File; // Photo as string (file path, URL, or base64) or File object
  studentSignature?: string | File; // Signature as string (file path, URL, or base64) or File object
  cwsnCode: string; // CWSN Code field
  sambalKramank: string; // Sambal Kramank field

  // Legacy fields - commented out as they're not in backend DTO
  // firstName: string;
  // lastName: string;
  // phone: string;
  // email: string;
  // guardianName: string;
  // guardianPhone: string;
  // guardianEmail: string;
  // active: boolean;
  // apparIdNo: string;
  // village: string;
  // password: string;
  // classId: number;
}

export interface StudentFormData extends Omit<Student, "id"> {
  id?: number;
  // Additional fields for form handling
  password?: string; // Optional for form data
  subjects: string | string[]; // Allow both string and array for flexibility
}

// ApplicationStudentDTO interface - matches exact backend DTO structure
export interface ApplicationStudentDTO {
  id?: number;
  studentCode?: string; // Student code field
  applicationNo: string;
  rollNumber: string;
  enrolmentNo: string;
  studentName: string; // Required field marked with *
  fatherName: string;
  motherName: string;
  dateOfBirth: string | number[]; // Can be string or array format [year, month, day]
  gender: string;
  medium: string;
  caste: string;
  subCaste: string;
  casteCertificate: string;
  religion: string;
  sssmid: string;
  scholarNo: string;
  penNo: string;
  apparId: string;
  aadharNo: string;
  houseNo: string;
  street: string;
  landmark: string;
  area: string;
  city: string;
  postOffice: string;
  district: string;
  state: string;
  pinCode: string;
  mobileNo: string;
  bankName: string;
  ifcCode: string;
  accountNo: string;
  annualIncome: string;
  section: string;
  subjects: string[] | string; // Backend expects List<String> - array of subject codes
  gmail: string; // Required field marked with *
  admissionDate: string | number[]; // Can be string or array format [year, month, day]
  admissionClass: string;
  admissionType: string; // Admission Type field (REGULAR or PRIVATE)
  stream: string; // Stream field
  schoolId: number; // Required field marked with *
  schoolClassId: number;
  sessionName: string;
  sessionId: number;
  className: string; // Required field marked with *
  userId?: number;
  status: boolean;
  studentPhoto?: string | File; // Photo as string (file path, URL, or base64) or File object
  studentSignature?: string | File; // Signature as string (file path, URL, or base64) or File object
  cwsnCode: string; // CWSN Code field
  sambalKramank: string; // Sambal Kramank field
}

const API_URL = `${process.env.REACT_APP_API_URL}/students`;

const getAuthToken = () => localStorage.getItem('token') || sessionStorage.getItem('token');
const getTenantSchema = () => localStorage.getItem('tenantSchema') || sessionStorage.getItem('tenantSchema');
const buildAuthHeaders = (): Record<string, string> => {
  const token = getAuthToken();
  if (!token) {
    throw new Error('No authentication token available');
  }
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`
  };
  const tenantSchema = getTenantSchema();
  if (tenantSchema) {
    headers['X-Tenant-ID'] = tenantSchema;
  }
  return headers;
};

// Fetch all students
const getAllStudents = async (): Promise<Student[]> => {
  const response = await axios.get(API_URL);
  return response.data;
};

// Fetch students by class key (supports className like "9th-ALL" and numeric ids where backend allows)
const getStudentsByClassName = async (classKey: string | number): Promise<Student[]> => {
  const safeKey = encodeURIComponent(String(classKey));
  const response = await axios.get(`${API_URL}/class/${safeKey}`);
  return response.data;
};

// Add this function for the new endpoint
const getStudentsByClassId = async (classId: number): Promise<Student[]> => {
  const response = await axios.get(`${API_URL}/class/id/${classId}`);
  return response.data;
};

// Get students by sessionId and classId
const getStudentsBySessionIdAndClassId = async (
  sessionId: number,
  classId: number
): Promise<Student[]> => {
  const response = await axios.get(`${API_URL}/sessionId/classId`, {
    params: {
      sessionId,
      classId
    }
  });
  return response.data;
};

const getStudentsBySessionIdAndClassName = async (
  sessionId: number,
  className: string
): Promise<Student[]> => {
  const safeClassName = encodeURIComponent(className);
  const response = await axios.get(
    `${API_URL}/sessionId/${sessionId}/className/${safeClassName}`
  );
  return response.data;
};

// Get Studets by sessionId and classId and Status
const getStudentsBySessionIdAndClassIdAndStatus = async (
  sessionId: number,
  classId: number,
): Promise<Student[]> => {
  const response = await axios.get(`${API_URL}/students/status/false`, {
    params: {
      sessionId,
      classId
    }
  });
  return response.data;
};

// Get students by roll number
const getStudentsByRollNumber = async (
  rollNumber: string
): Promise<Student[]> => {
  const response = await axios.get(`${API_URL}/rollNo/${rollNumber}`);
  return response.data;
};

// Get students by roll number and classId
const getStudentsByRollNumberAndClassId = async (
  rollNumber: string,
  classId: number
): Promise<Student[]> => {
  const response = await axios.get(
    `${API_URL}/rollNo/${rollNumber}/class/${classId}`
  );
  return response.data;
};

// Get student by ID
const getStudentById = async (studentId: number): Promise<Student> => {
  const response = await axios.get(`${API_URL}/${studentId}`);
  return response.data;
};

// Get student by Aadhar number
const getStudentByAadharNo = async (aadharNo: string): Promise<any> => {
  if (!aadharNo || aadharNo.trim() === '') {
    throw new Error('Aadhar number is required');
  }

  // Note: The tenant header should be set by axios interceptors or useTenantHeader
  // This function assumes the proper X-Tenant-ID header is already configured
  const response = await axios.get(`${API_URL}/aadharNo`, {
    params: { aadharNo: aadharNo.trim() }
  });
  return response.data;
};

// Create a new student
const createStudent = async (
  studentData: StudentFormData
): Promise<Student> => {
  if (!studentData.sessionId) throw new Error("Session ID is required");
  const response = await axios.post(API_URL, studentData);
  return response.data;
};

// Create a new student from application form (new DTO structure)
const createApplicationStudent = async (
  studentData: ApplicationStudentDTO
): Promise<Student> => {
  if (!studentData.sessionId) throw new Error("Session ID is required");
  if (!studentData.studentName?.trim()) throw new Error("Student name is required");
  if (!studentData.schoolId) throw new Error("School ID is required");

  // Helper function to ensure dates are in YYYY-MM-DD format (LocalDate format)
  const ensureLocalDate = (dateValue: string | number[] | null | undefined): string => {
    if (!dateValue) {
      const today = new Date();
      return today.toISOString().split('T')[0];
    }

    try {
      let dateString: string;

      // Handle array format [year, month, day]
      if (Array.isArray(dateValue)) {
        dateString = `${dateValue[0]}-${dateValue[1].toString().padStart(2, '0')}-${dateValue[2].toString().padStart(2, '0')}`;
      } else {
        dateString = dateValue;
      }

      // If it's already in YYYY-MM-DD format, return as is
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return dateString;
      }

      // If it's in dd/MM/yyyy format, convert to YYYY-MM-DD
      if (dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
        const [day, month, year] = dateString.split('/');
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }

      // Try to parse as Date and format
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }

      // If all parsing fails, return today's date as fallback
      const today = new Date();
      return today.toISOString().split('T')[0];
    } catch (error) {
      const today = new Date();
      return today.toISOString().split('T')[0];
    }
  };

  // Prepare the DTO with proper date formatting and handle photo field
  const formattedDTO: ApplicationStudentDTO = {
    ...studentData,
    dateOfBirth: ensureLocalDate(studentData.dateOfBirth),
    admissionDate: ensureLocalDate(studentData.admissionDate),
    // Ensure subjects is always an array
    subjects: (() => {
      if (Array.isArray(studentData.subjects)) {
        return studentData.subjects;
      }
      if (typeof studentData.subjects === 'string') {
        return studentData.subjects.split(',').filter(Boolean);
      }
      return [];
    })(),
    // Handle photo field - ensure it's a string value
    studentPhoto: typeof studentData.studentPhoto === 'string'
      ? studentData.studentPhoto || ''
      : '',
    // Handle signature field - ensure it's a string value
    studentSignature: typeof studentData.studentSignature === 'string'
      ? studentData.studentSignature || ''
      : '',
    // Ensure gmail field is never null/undefined - provide fallback
    gmail: studentData.gmail
  };

  // Additional validation to ensure no null values (but allow empty strings)
  const validateDto = (dto: any) => {
    const nullFields = [];
    for (const [key, value] of Object.entries(dto)) {
      if (value === null || value === undefined) {
        nullFields.push(key);
      }
    }
    if (nullFields.length > 0) {
      throw new Error(`Found null/undefined values in fields: ${nullFields.join(', ')}`);
    }
  };

  // Validate the DTO before sending
  validateDto(formattedDTO);

  try {
    // Send the DTO directly to the backend as JSON (matches @RequestBody StudentDto)
    const response = await axios.post(API_URL, formattedDTO);

    return response.data;
  } catch (error) {
    throw error;
  }
};

// Create bulk students
const createBulkStudents = async (
  studentDtos: ApplicationStudentDTO[]
): Promise<Student[]> => {
  const response = await axios.post(`${API_URL}/bulk-approve`, studentDtos);
  return response.data;
};

// Update an existing student
const updateStudent = async (
  id: number,
  studentData: StudentFormData
): Promise<Student> => {
  if (!studentData.sessionId) throw new Error("Session ID is required");
  const response = await axios.put(`${API_URL}/${id}`, studentData);
  return response.data;
};

// Update student using ApplicationStudentDTO structure with photo upload support
// IMPORTANT: For multipart uploads, exclude studentPhoto from JSON payload to prevent
// Jackson deserialization errors. The file is sent separately as multipartFile.
// Backend handles photo updates through the multipartFile parameter.
const updateStudentWithDTO = async (
  id: number,
  studentData: ApplicationStudentDTO
): Promise<Student> => {
  if (!studentData.sessionId) throw new Error("Session ID is required");
  if (!studentData.studentName?.trim()) throw new Error("Student name is required");
  if (!studentData.schoolId) throw new Error("School ID is required");

  // Helper function to ensure dates are in YYYY-MM-DD format (LocalDate format)
  const ensureLocalDate = (dateValue: string | number[] | null | undefined): string => {
    if (!dateValue) {
      const today = new Date();
      return today.toISOString().split('T')[0];
    }

    try {
      let dateString: string;

      // Handle array format [year, month, day]
      if (Array.isArray(dateValue)) {
        dateString = `${dateValue[0]}-${dateValue[1].toString().padStart(2, '0')}-${dateValue[2].toString().padStart(2, '0')}`;
      } else {
        dateString = dateValue;
      }

      // If it's already in YYYY-MM-DD format, return as is
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return dateString;
      }

      // If it's in dd/MM/yyyy format, convert to YYYY-MM-DD
      if (dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
        const [day, month, year] = dateString.split('/');
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }

      // Try to parse as Date and format
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }

      // If all parsing fails, return today's date as fallback
      const today = new Date();
      return today.toISOString().split('T')[0];
    } catch (error) {
      const today = new Date();
      return today.toISOString().split('T')[0];
    }
  };

  // Extract photo and signature files from studentData
  const { studentPhoto, studentSignature, ...studentDataWithoutPhoto } = studentData;

  // Prepare the DTO with proper date formatting
  // IMPORTANT: For multipart uploads, don't include studentPhoto in JSON payload
  // The file is sent separately as multipartFile, and including studentPhoto in JSON
  // causes Jackson deserialization errors when it's an empty object {}
  const formattedDTO: Omit<ApplicationStudentDTO, 'studentPhoto'> = {
    ...studentDataWithoutPhoto,
    dateOfBirth: ensureLocalDate(studentData.dateOfBirth),
    admissionDate: ensureLocalDate(studentData.admissionDate),
    // Ensure subjects is always an array
    subjects: (() => {
      if (Array.isArray(studentData.subjects)) {
        return studentData.subjects;
      }
      if (typeof studentData.subjects === 'string') {
        return studentData.subjects.split(',').filter(Boolean);
      }
      return [];
    })()
  };

  // Additional validation to ensure no null values (but allow empty strings)
  const validateDto = (dto: any) => {
    const nullFields = [];
    for (const [key, value] of Object.entries(dto)) {
      if (value === null || value === undefined) {
        nullFields.push(key);
      }
    }
    if (nullFields.length > 0) {
      throw new Error(`Found null/undefined values in fields: ${nullFields.join(', ')}`);
    }
  };

  // Validate the DTO before sending
  validateDto(formattedDTO);

  // Helper function to convert base64 string to File object
  const base64ToFile = (base64String: string, filename: string = 'photo.jpg'): File => {
    // Remove data URL prefix if present
    const base64Data = base64String.includes(',') ? base64String.split(',')[1] : base64String;
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new File([byteArray], filename, { type: 'image/jpeg' });
  };

  // Helper function to check if string is base64
  const isBase64 = (str: string): boolean => {
    try {
      return btoa(atob(str)) === str;
    } catch (err) {
      return false;
    }
  };

  // Helper function to check if value is a File object
  const isFile = (value: any): value is File => {
    return value && typeof value === 'object' && value.constructor === File;
  };

  try {
    // Create FormData for multipart request
    const formData = new FormData();

    // Add student data as JSON string (matches backend @RequestPart StudentDto studentDto)
    // This JSON should NOT include studentPhoto field to avoid Jackson deserialization errors
    const jsonBlob = new Blob([JSON.stringify(formattedDTO)], {
      type: 'application/json'
    });
    formData.append('studentDto', jsonBlob);

    // Handle photo file properly - only add if we have a valid file
    // Backend expects: either a valid MultipartFile or an empty file
    // Backend logic: if (multipartFile != null && !multipartFile.isEmpty()) -> process file
    //                else -> keep existing photo
    let hasValidPhotoFile = false;

    if (studentPhoto) {
      if (isFile(studentPhoto)) {
        // If it's already a File object, use it directly
        formData.append('multipartFile', studentPhoto);
        hasValidPhotoFile = true;
      } else if (typeof studentPhoto === 'string') {
        if (isBase64(studentPhoto)) {
          // If it's a base64 string, convert to File
          const file = base64ToFile(studentPhoto, 'student-photo.jpg');
          formData.append('multipartFile', file);
          hasValidPhotoFile = true;
        }
        // If it's a URL or path, don't add any file (backend will keep existing photo)
      }
    }

    // Only add empty file if we have no valid file to upload
    // This ensures backend receives either a valid file or no file at all
    if (!hasValidPhotoFile) {
      // Create a proper empty file that backend will recognize as "no file"
      const emptyFile = new File([], '', { type: 'application/octet-stream' });
      formData.append('multipartFile', emptyFile);
    }

    // Handle signature file properly - always add studentSignature part (required by backend)
    // Backend expects: either a valid MultipartFile or an empty file
    // Backend logic: if (studentSignature != null && !studentSignature.isEmpty()) -> process file
    //                else -> keep existing signature
    let hasValidSignatureFile = false;

    if (studentSignature) {
      if (isFile(studentSignature)) {
        // If it's already a File object, use it directly
        formData.append('studentSignature', studentSignature);
        hasValidSignatureFile = true;
      } else if (typeof studentSignature === 'string') {
        if (isBase64(studentSignature)) {
          // If it's a base64 string, convert to File
          const file = base64ToFile(studentSignature, 'student-signature.jpg');
          formData.append('studentSignature', file);
          hasValidSignatureFile = true;
        }
        // If it's a URL or path, don't add any file (backend will keep existing signature)
      }
    }

    // Always add studentSignature part (required by backend)
    // If no valid signature file, add empty file so backend knows to keep existing signature
    if (!hasValidSignatureFile) {
      // Create a proper empty file that backend will recognize as "no file"
      const emptySignatureFile = new File([], '', { type: 'application/octet-stream' });
      formData.append('studentSignature', emptySignatureFile);
    }



    // Send the FormData to the backend
    const response = await axios.put(`${API_URL}/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

// Delete a student
const deleteStudent = async (id: number): Promise<void> => {
  await axios.delete(`${API_URL}/${id}`);
};

// Revert student promotion (demote student) - DELETE /students/revert-promote/{studentId}
const revertPromotion = async (studentId: number): Promise<Student> => {
  const headers = buildAuthHeaders();
  const response = await axios.delete(`${API_URL}/revert-promote/${studentId}`, {
    headers,
  });
  return response.data;
};

// Update roll number and scholar number for multiple students
export interface StudentRollScholarUpdateDto {
  id: number;
  mobileNo: string;
  schoolClassId: number;
  sessionId: number;
  rollNumber: string;
  scholarNo: string;
}

const updateRollNumberAndScholarNumber = async (
  students: StudentRollScholarUpdateDto[]
): Promise<Student[]> => {
  const response = await axios.put(`${API_URL}/rollNo-scholarNo`, students);
  return response.data;
};

// Update student user status (disable/enable profile)
const updateStudentUserStatus = async (studentId: number): Promise<string> => {
  const response = await axios.put(`${API_URL}/user-status/${studentId}`);

  // Backend returns ResponseEntity<String>, so response.data should be the string message
  // Handle both direct string and potential object wrapper
  if (typeof response.data === 'string') {
    return response.data;
  } else if (response.data?.message) {
    return response.data.message;
  } else if (response.data?.data) {
    return response.data.data;
  } else {
    // Fallback: convert to string
    return String(response.data);
  }
};

const getStudentByEmail = async (email: string): Promise<Student> => {
  const response = await axios.get(`${API_URL}/email/${email}`);
  return response.data;
};

const getStudentByStudentCode = async (studentCode: string): Promise<Student> => {
  const response = await axios.get(`${API_URL}/studentCode`, {
    params: { studentCode }
  });
  return response.data;
};

// Bulk: Get students by studentCodes (new endpoint)
const getStudentsByStudentCodesBulk = async (studentCodes: string[]): Promise<Student[]> => {
  const headers = buildAuthHeaders();
  const response = await axios.post(`${API_URL}/bulk-student-code`, studentCodes, { headers });
  return response.data;
};

// Get student by mobile number
export const getStudentByMobileNo = async (
  mobile: string,
  sessionId: number,
  classId: number
): Promise<Student> => {
  const headers = buildAuthHeaders();
  // Ensure X-Tenant-ID is included - check multiple sources
  let tenantId = headers['X-Tenant-ID'];

  // Check axios defaults first (most reliable)
  if (!tenantId && axios.defaults.headers.common['X-Tenant-ID']) {
    tenantId = String(axios.defaults.headers.common['X-Tenant-ID']);
  }

  // Check storage if still not found
  if (!tenantId) {
    const tenantSchema = getTenantSchema();
    if (tenantSchema) {
      tenantId = tenantSchema;
    }
  }

  // If we still don't have tenant ID, set it from axios defaults (might be set by interceptors)
  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId;
    // Also ensure it's in axios defaults for future requests
    axios.defaults.headers.common['X-Tenant-ID'] = tenantId;
  }

  const response = await axios.get(`${API_URL}/mobileNo`, {
    params: { mobile, sessionId, classId },
    headers
  });
  return response.data;
};

// Get student photo info by student ID
// Response includes: { fileName: string; filePath: string; signature?: string }
const getStudentPhotoInfo = async (id: number): Promise<{ fileName: string; filePath: string; signature?: string } | null> => {
  try {
    const headers = buildAuthHeaders();
    const response = await axios.get(`${API_URL}/${id}/studentPhoto`, { headers });
    return response.data;
  } catch (error: any) {
    // Handle 404 gracefully - student might not have a photo, which is normal
    if (error?.response?.status === 404) {
      return null;
    }
    // Re-throw other errors (401/403 will be handled by axios interceptor)
    throw error;
  }
};

export interface BulkStudentPhotoInfo {
  studentId: string;
  fileName: string;
  signature: string;
  filePath: string;
}

// Bulk: Get photo info for multiple studentIds (new endpoint)
const getStudentPhotoInfosBulk = async (studentIds: number[]): Promise<BulkStudentPhotoInfo[]> => {
  const headers = buildAuthHeaders();
  const response = await axios.post(`${API_URL}/bulk-studentPhoto`, studentIds, { headers });
  return response.data;
};

// Download students Excel function
const downloadStudentsExcel = async (params: { schoolId: number; classId: number; sessionId: number; sortBy?: string }): Promise<void> => {
  const response = await axios.get(`${API_URL}/export`, {
    params: {
      schoolId: params.schoolId,
      classId: params.classId,
      sessionId: params.sessionId,
      sortBy: params.sortBy || 'rollnumber'
    },
    responseType: 'blob', // Important for file downloads
  });

  // Create blob from response data
  const blob = new Blob([response.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });

  // Extract filename from Content-Disposition header or create default
  const contentDisposition = response.headers['content-disposition'];
  let filename = `STUDENTS_${params.classId}_${params.sessionId}.xlsx`;

  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1].replace(/['"]/g, '');
    }
  }

  // Create download link and trigger download
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// Fetch student photo blob by student ID using existing API structure
const getStudentPhotoBlob = async (studentId: number): Promise<Blob | null> => {
  const headers = buildAuthHeaders();

  const photoInfo = await getStudentPhotoInfo(studentId);

  if (!photoInfo?.fileName) {
    // Return null instead of throwing - student might not have a photo
    return null;
  }

  const constructedUrl = `${process.env.REACT_APP_API_URL}/students/photo/${photoInfo.fileName}`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000);

  try {
    const response = await fetch(constructedUrl, {
      headers,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    // Handle 404 gracefully - student might not have a photo
    if (response.status === 404) {
      return null;
    }

    // Handle 401/403 gracefully - don't throw, return null
    if (response.status === 401 || response.status === 403) {
      return null;
    }

    if (!response.ok) {
      // For other errors, log but don't throw to prevent logout
      return null;
    }

    const blob = await response.blob();
    return blob;
  } catch (error) {
    clearTimeout(timeoutId);
    // Don't throw errors - return null instead to prevent logout
    return null;
  }
};

// Export the photo info hook separately for easier importing
// Response includes signature path in signature field
export const useStudentPhotoInfo = (studentId?: number) => {
  return useQuery<{ fileName: string; filePath: string; signature?: string } | null, Error>(
    ["studentPhotoInfo", studentId],
    () => getStudentPhotoInfo(studentId!),
    {
      enabled: !!studentId,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      refetchOnWindowFocus: false,
      retry: (failureCount, error: any) => {
        // Don't retry on 404 (student has no photo) or 401/403 (auth errors)
        if (error?.response?.status === 404 || error?.response?.status === 401 || error?.response?.status === 403) {
          return false;
        }
        // Retry other errors up to 1 time
        return failureCount < 1;
      },
    }
  );
};

// Custom hook for fetching student photo with blob URL management
export const useStudentPhoto = (studentId?: number) => {
  const [authenticatedImageUrl, setAuthenticatedImageUrl] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!studentId) {
      setAuthenticatedImageUrl(null);
      setImageError(false);
      setIsLoading(false);
      return;
    }

    const fetchPhoto = async () => {
      try {
        setIsLoading(true);
        setImageError(false);

        const blob = await getStudentPhotoBlob(studentId);
        if (blob) {
          const objectUrl = URL.createObjectURL(blob);
          setAuthenticatedImageUrl(objectUrl);
        } else {
          // No photo available - this is normal, not an error
          setAuthenticatedImageUrl(null);
          setImageError(false);
        }
      } catch (error) {
        // Silently handle errors - don't set error state to prevent UI issues
        setAuthenticatedImageUrl(null);
        setImageError(false); // Don't show error for missing photos
      } finally {
        setIsLoading(false);
      }
    };

    fetchPhoto();

    // Cleanup function to revoke object URL
    return () => {
      if (authenticatedImageUrl) {
        URL.revokeObjectURL(authenticatedImageUrl);
      }
    };
  }, [studentId]);

  // Cleanup object URL on unmount
  useEffect(() => {
    return () => {
      if (authenticatedImageUrl) {
        URL.revokeObjectURL(authenticatedImageUrl);
      }
    };
  }, [authenticatedImageUrl]);

  return {
    imageUrl: authenticatedImageUrl,
    isLoading,
    error: imageError,
    hasPhoto: !!authenticatedImageUrl && !imageError
  };
};

// Fetch student signature blob by student ID using existing API structure
// The signature path is available in getStudentPhotoInfo response
// Signature uses the same /students/photo/{fileName} endpoint as photo, but with signature filename
const getStudentSignatureBlob = async (studentId: number): Promise<Blob | null> => {
  const headers = buildAuthHeaders();

  const photoInfo = await getStudentPhotoInfo(studentId);

  if (!photoInfo?.signature) {
    // Return null instead of throwing - student might not have a signature
    return null;
  }

  // Use the same photo endpoint with signature filename (as seen in StudentFormModal.tsx)
  const constructedUrl = `${process.env.REACT_APP_API_URL}/students/photo/${photoInfo.signature}`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000);

  try {
    const response = await fetch(constructedUrl, {
      headers,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    // Handle 404 gracefully - student might not have a signature
    if (response.status === 404) {
      return null;
    }

    // Handle 401/403 gracefully - don't throw, return null
    if (response.status === 401 || response.status === 403) {
      return null;
    }

    if (!response.ok) {
      // For other errors, log but don't throw to prevent logout
      return null;
    }

    return await response.blob();
  } catch (error) {
    clearTimeout(timeoutId);
    // Don't throw errors - return null instead to prevent logout
    return null;
  }
};

// Custom hook for fetching student signature with blob URL management
export const useStudentSignature = (studentId?: number) => {
  const [authenticatedSignatureUrl, setAuthenticatedSignatureUrl] = useState<string | null>(null);
  const [signatureError, setSignatureError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!studentId) {
      setAuthenticatedSignatureUrl(null);
      setSignatureError(false);
      setIsLoading(false);
      return;
    }

    const fetchSignature = async () => {
      try {
        setIsLoading(true);
        setSignatureError(false);

        const blob = await getStudentSignatureBlob(studentId);
        if (blob) {
          const objectUrl = URL.createObjectURL(blob);
          setAuthenticatedSignatureUrl(objectUrl);
        } else {
          // No signature available - this is normal, not an error
          setAuthenticatedSignatureUrl(null);
          setSignatureError(false);
        }
      } catch (error) {
        // Silently handle errors - don't set error state to prevent UI issues
        setAuthenticatedSignatureUrl(null);
        setSignatureError(false); // Don't show error for missing signatures
      } finally {
        setIsLoading(false);
      }
    };

    fetchSignature();

    // Cleanup function to revoke object URL
    return () => {
      if (authenticatedSignatureUrl) {
        URL.revokeObjectURL(authenticatedSignatureUrl);
      }
    };
  }, [studentId]);

  // Cleanup object URL on unmount
  useEffect(() => {
    return () => {
      if (authenticatedSignatureUrl) {
        URL.revokeObjectURL(authenticatedSignatureUrl);
      }
    };
  }, [authenticatedSignatureUrl]);

  return {
    signatureUrl: authenticatedSignatureUrl,
    isLoading,
    error: signatureError,
    hasSignature: !!authenticatedSignatureUrl && !signatureError
  };
};

// Export the function for direct use
export { getStudentPhotoBlob, getStudentSignatureBlob };

// Bulk promote students
export interface PromoteBulkParams {
  studentIds: number[];
  newClassId: number;
  sessionId: number;
  marksheetType: string;
  schoolType: string;
  withSubjects?: boolean;
}

const promoteBulkStudent = async (params: PromoteBulkParams): Promise<Student[]> => {
  const headers = buildAuthHeaders();
  const response = await axios.post(`${API_URL}/promote-bulk`, params.studentIds, {
    params: {
      studentIds: params.studentIds.join(','),
      newClassId: params.newClassId,
      sessionId: params.sessionId,
      marksheetType: params.marksheetType,
      schoolType: params.schoolType,
      withSubjects: params.withSubjects ?? false,
    },
    headers
  });
  return response.data;
};

// DTO for student promotion request tracking
export interface StudentPromoteTrackDto {
  id?: number;
  studentId: number;
  studentName: string;
  classId: number;
  sessionId: number;
  schoolType: string; // SAME or OTHER
  prevClass?: number; // Previous class (current selected class in layout)
}

/** Matches backend PromoteMappingDto — class-level promotion rules */
export interface PromoteMappingDto {
  id: number;
  fromClass: string;
  toClass: string;
  enableSubjectSelection: boolean;
}

const promoteRequest = async (
  dto: StudentPromoteTrackDto
): Promise<StudentPromoteTrackDto> => {
  const headers = buildAuthHeaders();
  const response = await axios.post(`${API_URL}/promote-request`, dto, { headers });
  return response.data;
};

// Update promotion request
const updatePromoteRequest = async (
  id: number,
  dto: StudentPromoteTrackDto
): Promise<StudentPromoteTrackDto> => {
  const headers = buildAuthHeaders();
  const response = await axios.put(`${API_URL}/promote-request/${id}`, dto, { headers });
  return response.data;
};

// Get promotion track by sessionId, classId, and prevClass
const getPromoteTrackBySessionAndClass = async (
  sessionId: number,
  classId: number,
  prevClass: number
): Promise<StudentPromoteTrackDto[]> => {
  try {
    const headers = buildAuthHeaders();
    const response = await axios.get(`${API_URL}/promote-request`, {
      params: { sessionId, classId, prevClass },
      headers
    });
    // Handle both single object and array responses
    const data = response.data;
    return Array.isArray(data) ? data : [data];
  } catch (error: any) {
    // If 404, return empty array (will show "No Student Promote Request Found")
    if (error?.response?.status === 404) {
      return [];
    }
    // Re-throw other errors
    throw error;
  }
};

const getPromoteMapping = async (className: string): Promise<PromoteMappingDto[]> => {
  try {
    const headers = buildAuthHeaders();
    const response = await axios.get(
      `${API_URL}/promote-mapping/${encodeURIComponent(className)}`,
      { headers }
    );
    const data = response.data;
    return Array.isArray(data) ? data : data ? [data] : [];
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return [];
    }
    throw error;
  }
};

const useStudentByEmail = (email?: string) =>
  useQuery<Student, Error>(
    ["studentByEmail", email],
    () => getStudentByEmail(email!),
    { enabled: !!email }
  );

export function useStudents(tenantLoading: boolean = false, schoolId?: number, enabled: boolean = true) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAppUser = user?.roles?.includes('APP_USER');

  // Clear all school-specific caches when schoolId changes
  useEffect(() => {
    if (schoolId) {
      // Remove all other school caches to prevent cross-school data
      queryClient.removeQueries({
        queryKey: ["students"],
        predicate: (query) => {
          // For students, we need to check if the data contains students from other schools
          const queryData = query.state.data as Student[] | undefined;
          if (queryData && Array.isArray(queryData)) {
            return queryData.some(student => student.schoolId !== schoolId);
          }
          return false;
        }
      });
    }
  }, [schoolId, queryClient]);

  // Disabled getAll query - now using sessionId/classId specific query
  // Also disable for APP_USER to prevent unnecessary API calls
  const studentsQuery = useQuery<Student[], Error>({
    queryKey: ["students", schoolId],
    queryFn: getAllStudents,
    enabled: false, // Disabled - using useStudentsBySessionIdAndClassId instead
    staleTime: 0, // Always fetch fresh data when schoolId changes
    refetchOnMount: !isAppUser, // Don't refetch on mount for APP_USER
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
  });

  // Renamed to useStudentsByClassName to follow React hook naming convention
  const useStudentsByClassName = (classKey: string | number) =>
    useQuery<Student[], Error>({
      queryKey: ["students", "classKey", classKey],
      queryFn: () => getStudentsByClassName(classKey),
      enabled: !!classKey && !tenantLoading,
    });

  // Add this hook for classId-based fetching
  const useStudentsByClassId = (classId: number) =>
    useQuery<Student[], Error>({
      queryKey: ["students", "classId", classId],
      queryFn: () => getStudentsByClassId(classId),
      enabled: Number(classId) > 0 && !tenantLoading,
    });

  // Hook to fetch by sessionId and classId
  const useStudentsBySessionIdAndClassId = (sessionId?: number, classId?: number) =>
    useQuery<Student[], Error>({
      queryKey: ["studentsBySessionIdAndClassId", sessionId, classId],
      queryFn: () => {
        if (!sessionId || !classId || classId <= 0) return Promise.resolve([]);
        return getStudentsBySessionIdAndClassId(sessionId, classId);
      },
      enabled: !!sessionId && !!classId && classId > 0 && !tenantLoading,
      staleTime: 0,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    });

  const useStudentsBySessionIdAndClassName = (sessionId?: number, className?: string) =>
    useQuery<Student[], Error>({
      queryKey: ["studentsBySessionIdAndClassName", sessionId, className],
      queryFn: () => {
        if (!sessionId || !className) return Promise.resolve([]);
        return getStudentsBySessionIdAndClassName(sessionId, className);
      },
      enabled: !!sessionId && !!className && !tenantLoading,
      staleTime: 0,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    });

  const useStudentsBySessionIdAndClassIdAndStatus = (sessionId?: number, classId?: number) =>
    useQuery<Student[], Error>({
      queryKey: ["studentsBySessionIdAndClassIdAndStatus", sessionId, classId],
      queryFn: () => {
        if (!sessionId || !classId) return Promise.resolve([]);
        return getStudentsBySessionIdAndClassIdAndStatus(sessionId, classId);
      },
      enabled: !!sessionId && !!classId && !tenantLoading,
      staleTime: 0,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    });

  const createStudentMutation = useMutation({
    mutationFn: createStudent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["students"] });
      queryClient.invalidateQueries({ queryKey: ["studentClassMapping"] });
      if (schoolId) {
        queryClient.invalidateQueries({ queryKey: ["students", schoolId] });
      }
    },
  });

  const createApplicationStudentMutation = useMutation({
    mutationFn: createApplicationStudent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["students"] });
      queryClient.invalidateQueries({ queryKey: ["studentClassMapping"] });
      if (schoolId) {
        queryClient.invalidateQueries({ queryKey: ["students", schoolId] });
      }
    },
  });

  const createBulkStudentMutation = useMutation({
    mutationFn: createBulkStudents,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["students"] });
      queryClient.invalidateQueries({ queryKey: ["studentClassMapping"] });
      if (schoolId) {
        queryClient.invalidateQueries({ queryKey: ["students", schoolId] });
      }
    },
  });

  const updateStudentMutation = useMutation(
    (args: { id: number; data: StudentFormData }) =>
      updateStudent(args.id, args.data),
    {
      onSuccess: () => {
        // Invalidate all relevant student queries
        queryClient.invalidateQueries({ queryKey: ["students"] });
        queryClient.invalidateQueries({ queryKey: ["studentClassMapping"] });
        queryClient.invalidateQueries({ queryKey: ["tclist"] }); // Invalidate TC list to show updated students
        if (schoolId) {
          queryClient.invalidateQueries({ queryKey: ["students", schoolId] });
        }
        // This will invalidate all studentClassMapping queries including the one used in StudentManagement
      },
    }
  );

  const updateStudentWithDTOMutation = useMutation(
    (args: { id: number; data: ApplicationStudentDTO }) =>
      updateStudentWithDTO(args.id, args.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["students"] });
        queryClient.invalidateQueries({ queryKey: ["studentClassMapping"] });
        queryClient.invalidateQueries({ queryKey: ["tclist"] }); // Invalidate TC list to show updated students
        if (schoolId) {
          queryClient.invalidateQueries({ queryKey: ["students", schoolId] });
        }
      },
    }
  );

  const deleteStudentMutation = useMutation({
    mutationFn: deleteStudent,
    onSuccess: () => {
      // Invalidate all student-related queries to ensure UI updates
      queryClient.invalidateQueries({ queryKey: ["students"] });
      queryClient.invalidateQueries({ queryKey: ["studentClassMapping"] });
      if (schoolId) {
        queryClient.invalidateQueries({ queryKey: ["students", schoolId] });
      }
    },
  });

  // Mutation for reverting student promotion (demote student)
  const revertPromotionMutation = useMutation({
    mutationFn: revertPromotion,
    onSuccess: () => {
      // Invalidate relevant queries so UI reflects updated class/session and promotion state
      queryClient.invalidateQueries({ queryKey: ["students"] });
      queryClient.invalidateQueries({ queryKey: ["studentClassMapping"] });
      queryClient.invalidateQueries({ queryKey: ["promoteTrackBySessionClass"] });
      queryClient.invalidateQueries({ queryKey: ["marksheet", "studentId"] });
      if (schoolId) {
        queryClient.invalidateQueries({ queryKey: ["students", schoolId] });
      }
    },
  });

  // Mutation for download students Excel
  const downloadStudentsMutation = useMutation({
    mutationFn: downloadStudentsExcel,
    onError: (error) => {
    },
  });

  // Mutation for updating student user status
  const updateStudentUserStatusMutation = useMutation({
    mutationFn: updateStudentUserStatus,
    onSuccess: () => {
      // Invalidate all student-related queries to ensure UI updates
      queryClient.invalidateQueries({ queryKey: ["students"] });
      queryClient.invalidateQueries({ queryKey: ["studentClassMapping"] });
      if (schoolId) {
        queryClient.invalidateQueries({ queryKey: ["students", schoolId] });
      }
    },
  });

  // Mutation for updating roll number and scholar number
  const updateRollNumberAndScholarNumberMutation = useMutation({
    mutationFn: updateRollNumberAndScholarNumber,
    onSuccess: () => {
      // Invalidate all student-related queries to ensure UI updates
      queryClient.invalidateQueries({ queryKey: ["students"] });
      queryClient.invalidateQueries({ queryKey: ["studentClassMapping"] });
      if (schoolId) {
        queryClient.invalidateQueries({ queryKey: ["students", schoolId] });
      }
    },
  });

  const promoteBulkStudentMutation = useMutation({
    mutationFn: promoteBulkStudent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["students"] });
      queryClient.invalidateQueries({ queryKey: ["studentClassMapping"] });
      if (schoolId) {
        queryClient.invalidateQueries({ queryKey: ["students", schoolId] });
      }
    },
  });

  const promoteRequestMutation = useMutation({
    mutationFn: promoteRequest,
    onSuccess: () => {
      // Invalidate promote track query to refresh data
      queryClient.invalidateQueries({ queryKey: ["promoteTrackBySessionClass"] });
    },
  });

  const updatePromoteRequestMutation = useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: StudentPromoteTrackDto }) => updatePromoteRequest(id, dto),
    onSuccess: () => {
      // Invalidate promote track query to refresh data
      queryClient.invalidateQueries({ queryKey: ["promoteTrackBySessionClass"] });
    },
  });

  // Hook to get promotion track by sessionId, classId, and prevClass
  const useGetPromoteTrackBySessionClass = (
    sessionId?: number,
    classId?: number,
    prevClass?: number
  ) =>
    useQuery<StudentPromoteTrackDto[], Error>({
      queryKey: ["promoteTrackBySessionClass", sessionId, classId, prevClass],
      queryFn: () => getPromoteTrackBySessionAndClass(sessionId!, classId!, prevClass!),
      enabled: !!sessionId && !!classId && !!prevClass && !tenantLoading,
      retry: false, // Don't retry on 404
    });

  /** GET /students/promote-mapping/{className} — promotion rules for the source class */
  const usePromoteMapping = (className?: string) =>
    useQuery<PromoteMappingDto[], Error>({
      queryKey: ["promoteMapping", className],
      queryFn: () => getPromoteMapping(className!),
      enabled: !!className && className.trim() !== "" && !tenantLoading,
      retry: false,
    });

  // Hook to get students by roll number
  const useStudentsByRollNumber = (rollNumber?: string) =>
    useQuery<Student[], Error>(
      ["students", "rollNumber", rollNumber],
      () => getStudentsByRollNumber(rollNumber!),
      { enabled: !!rollNumber && !tenantLoading }
    );

  // Hook to get students by roll number and classId
  const useStudentsByRollNumberAndClassId = (
    rollNumber?: string,
    classId?: number
  ) =>
    useQuery<Student[], Error>(
      ["students", "rollNumber", rollNumber, "classId", classId],
      () => getStudentsByRollNumberAndClassId(rollNumber!, classId!),
      { enabled: !!rollNumber && !!classId && !tenantLoading }
    );

  // Hook to get student by Aadhar number
  const useStudentByAadharNo = (aadharNo?: string) =>
    useQuery<any, Error>({
      queryKey: ["studentByAadharNo", aadharNo],
      queryFn: () => getStudentByAadharNo(aadharNo!),
      enabled: !!aadharNo && aadharNo.trim() !== '' && !tenantLoading,
      retry: false,
    });

  // Hook to get student by mobile number
  const useStudentByMobileNo = (
    mobile?: string,
    sessionId?: number,
    classId?: number
  ) => {
    // Validate that mobile is actually a mobile number (not an email)
    // Mobile numbers are typically 10 digits and don't contain '@'
    const isValidMobile = mobile &&
      !mobile.includes('@') &&
      /^\d{10}$/.test(mobile.replace(/\D/g, ''));

    return useQuery<Student, Error>({
      queryKey: ["studentByMobileNo", mobile, sessionId, classId],
      queryFn: () => getStudentByMobileNo(mobile!, sessionId!, classId!),
      enabled: !!isValidMobile && !!sessionId && !!classId && !tenantLoading,
      retry: false,
    });
  };

  // Hook to get student by ID
  const useStudentById = (studentId?: number) =>
    useQuery<Student, Error>({
      queryKey: ["studentById", studentId],
      queryFn: () => getStudentById(studentId!),
      enabled: !!studentId && !tenantLoading,
    });


  return {
    studentsQuery,
    useStudentsByClassName,
    useStudentsByClassId,
    useStudentsBySessionIdAndClassId,
    useStudentsBySessionIdAndClassName,
    useStudentsBySessionIdAndClassIdAndStatus,
    createStudentMutation,
    createApplicationStudentMutation,
    createBulkStudentMutation,
    updateStudentMutation,
    updateStudentWithDTOMutation,
    deleteStudentMutation,
    downloadStudentsMutation,
    updateStudentUserStatusMutation,
    updateRollNumberAndScholarNumberMutation,
    promoteBulkStudentMutation,
    promoteRequestMutation,
    updatePromoteRequestMutation,
    revertPromotionMutation,
    useGetPromoteTrackBySessionClass,
    usePromoteMapping,
    getStudentByEmail,
    useStudentByEmail,
    useStudentsByRollNumber,
    useStudentsByRollNumberAndClassId,
    useStudentByAadharNo,
    useStudentByMobileNo,
    useStudentById,
    getStudentByStudentCode,
    getStudentsByStudentCodesBulk,
    getStudentPhotoInfosBulk,
    getStudentByMobileNo,
  };
}
